import { useEffect, useState } from 'react'
import {
  abandonWorkingDirectory,
  fetchWorkingDirectories,
  updateWorkingDirectory,
} from '../../api/ws'
import { useAppStore } from '../../store/app'
import type { WorkingDirectory } from '../../store/types'

// Temporary CRUD view for working directories in the caller's current
// org. Mirrors HostsSheet/DevicesSheet/PositionsSheet. Reachable via
// gear → About → Debug → "Working Directories".
//
// Inline edit on directory_path is allowed; server rejects on
// collision (another live wd already owns that path on the same host)
// and we surface the error. Abandon soft-deletes via abandoned_at;
// has_live_dependents on live positions/agents prompts a cascade
// confirmation that retires + eliminates them in the same transaction.
export function WorkingDirectoriesSheet() {
  const wds = useAppStore((s) => s.workingDirectories)
  const orgs = useAppStore((s) => s.availableOrganizations)
  const currentOrg = orgs.find((o) => o.is_current)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (currentOrg?.id) fetchWorkingDirectories(currentOrg.id)
  }, [currentOrg?.id])

  function handleError(e: unknown) {
    setError(typeof e === 'string' ? e : (e as Error)?.message ?? String(e))
  }

  function refresh() {
    if (currentOrg?.id) fetchWorkingDirectories(currentOrg.id)
  }

  if (!currentOrg) {
    return (
      <div style={{ padding: '12px 16px', fontSize: 14 }}>
        <p style={{ opacity: 0.6 }}>No current household.</p>
      </div>
    )
  }

  // Hide abandoned rows by default; the list query returns them so
  // operators can confirm what's gone, but a debug surface that
  // surfaces dead rows is noise. Toggle later if useful.
  const live = wds.filter((w) => !w.abandoned_at)

  return (
    <div style={{ padding: '12px 16px', fontSize: 14 }}>
      <p style={{ opacity: 0.65, marginTop: 0 }}>
        Working directories are the filesystem roots agents operate in.
        Each path is unique per host — collisions are refused. "Abandon"
        soft-deletes and cascades through dependent positions and agents
        if you confirm.
      </p>

      {error && (
        <div
          style={{
            padding: '8px 12px',
            background: '#fee',
            color: '#900',
            borderRadius: 4,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      {live.length === 0 ? (
        <p style={{ opacity: 0.6 }}>No working directories in this organization.</p>
      ) : (
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {live.map((w) => (
            <WorkingDirectoryRow
              key={w.id}
              wd={w}
              onError={handleError}
              onClearError={() => setError(null)}
              onMutated={refresh}
            />
          ))}
        </ul>
      )}
    </div>
  )
}

function WorkingDirectoryRow({
  wd,
  onError,
  onClearError,
  onMutated,
}: {
  wd: WorkingDirectory
  onError: (e: unknown) => void
  onClearError: () => void
  onMutated: () => void
}) {
  const [pathDraft, setPathDraft] = useState(wd.directory_path)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    setPathDraft(wd.directory_path)
  }, [wd.directory_path])

  const pathDirty = pathDraft.trim() !== wd.directory_path

  async function savePath() {
    if (!pathDirty) return
    onClearError()
    setBusy(true)
    try {
      const resp = await updateWorkingDirectory({
        id: wd.id,
        hostID: wd.host_id,
        directoryPath: pathDraft.trim(),
      })
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  async function handleAbandon() {
    if (!confirm(`Abandon working directory "${wd.directory_path}"?`)) return
    onClearError()
    setBusy(true)
    try {
      let resp = await abandonWorkingDirectory({ id: wd.id })
      if (resp?.error === 'has_live_dependents') {
        const positions = resp.blockers?.positions ?? []
        const agentCount = positions.reduce(
          (n, p) => n + (p.agents?.length ?? 0),
          0,
        )
        const lines = positions.map(
          (p) =>
            `• position ${p.name}` +
            (p.agents?.length ? ` (${p.agents.length} agent(s))` : ''),
        ).join('\n')
        const ok = confirm(
          `This directory has ${positions.length} live position(s) ` +
            `and ${agentCount} agent(s):\n${lines}\n\n` +
            `Retire the agents, eliminate the positions, and abandon the directory?`,
        )
        if (!ok) {
          setBusy(false)
          return
        }
        resp = await abandonWorkingDirectory({ id: wd.id, cascade: true })
      }
      if (resp?.error) {
        onError(resp.error)
        return
      }
      onMutated()
    } catch (e) {
      onError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <li
      style={{
        padding: '12px 0',
        borderBottom: '1px solid #eee',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ opacity: 0.6, fontSize: 12 }}>
        on {wd.hostname || wd.host_id.slice(0, 8) + '…'}
        {wd.is_temp ? ' · temp' : ''}
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <span style={{ fontSize: 12, opacity: 0.7 }}>Directory path</span>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={pathDraft}
            onChange={(e) => setPathDraft(e.target.value)}
            style={{ flex: 1, fontSize: 16, fontFamily: 'monospace' }}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          <button onClick={savePath} disabled={busy || !pathDirty}>
            Save
          </button>
        </div>
      </label>

      <div>
        <button
          onClick={handleAbandon}
          disabled={busy}
          style={{ color: '#900' }}
        >
          Abandon
        </button>
      </div>
    </li>
  )
}
